/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { format } from 'date-fns';
import type { Transaction } from '../../types';
import { formatINR, Tag } from '../console/ui';
import { ChevronDownIc } from '../console/icons';
import {
  ChargeGroup,
  DisplayCurrency,
  groupDetail,
  parseDescription,
  PRODUCTS,
  productOf,
  txAmountValue,
} from './billingUtils';

const signed = (type: 'credit' | 'debit', s: string) => (type === 'credit' ? `+${s}` : `−${s.replace(/^−/, '')}`);

const amountCls = (type: 'credit' | 'debit') => (type === 'credit' ? 'text-ps-ok' : 'text-ps-text');

/** Money for a group: sum per-row so USD uses each payment's stored rate. */
const groupAmount = (g: ChargeGroup, currency: DisplayCurrency, rate: number) =>
  formatINR(
    g.items.reduce((s, tx) => s + txAmountValue(tx, currency, rate), 0),
    currency,
  );

const txAmount = (tx: Transaction, currency: DisplayCurrency, rate: number) =>
  formatINR(txAmountValue(tx, currency, rate), currency);

/**
 * Charges table. In grouped mode each per-minute resource collapses into one
 * row per day and can be expanded to show the raw lines. In raw mode every
 * transaction is a row.
 */
const ChargesTable: React.FC<{
  groups?: ChargeGroup[];
  raw?: Transaction[];
  currency: DisplayCurrency;
  rate: number;
  footer?: React.ReactNode;
  /** Column order: "date-first" (transactions) or "resource-first" (overview). */
  variant?: 'date-first' | 'resource-first';
  expandable?: boolean;
}> = ({ groups, raw, currency, rate, footer, variant = 'date-first', expandable = true }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (k: string) => setOpen((s) => ({ ...s, [k]: !s[k] }));
  const dateFirst = variant === 'date-first';

  const head = dateFirst ? (
    <tr>
      <th>Date</th>
      <th>Resource</th>
      <th>Product</th>
      <th>Detail</th>
      <th className="!text-right">Amount</th>
    </tr>
  ) : (
    <tr>
      <th>Resource</th>
      <th>Product</th>
      <th>Period</th>
      <th>Detail</th>
      <th className="!text-right">Amount</th>
    </tr>
  );

  const rows: React.ReactNode[] = [];
  if (raw) {
    raw.forEach((tx) => {
      const p = parseDescription(tx.description);
      const prod = PRODUCTS[productOf(tx)];
      const date = <td className="text-ps-muted">{format(new Date(tx.created_at), 'd MMM, HH:mm')}</td>;
      const resource = (
        <td className={`max-w-[360px] truncate ${p.mono && tx.type === 'debit' ? 'font-mono' : ''}`} title={tx.description}>
          {tx.type === 'credit' ? tx.description : p.resource}
        </td>
      );
      const product = (
        <td>
          <Tag tone={prod.tone}>{prod.label}</Tag>
        </td>
      );
      const detail = (
        <td className="text-ps-muted max-w-[360px] truncate" title={p.spec}>
          {[p.advance ? 'Advance' : '', p.spec || ''].filter(Boolean).join(' · ') || '-'}
        </td>
      );
      rows.push(
        <tr key={tx.id}>
          {dateFirst ? (
            <>
              {date}
              {resource}
              {product}
            </>
          ) : (
            <>
              {resource}
              {product}
              {date}
            </>
          )}
          {detail}
          <td className={`text-right ${amountCls(tx.type)}`}>{signed(tx.type, txAmount(tx, currency, rate))}</td>
        </tr>,
      );
    });
  } else {
    (groups || []).forEach((g) => {
      const prod = PRODUCTS[g.product];
      const canExpand = expandable && g.items.length > 1;
      const isOpen = !!open[g.key];
      const date = <td className="text-ps-muted">{format(g.day, 'd MMM')}</td>;
      const resource = (
        <td className={`max-w-[360px] truncate ${g.mono ? 'font-mono' : ''}`} title={g.resource}>
          {canExpand ? (
            <button
              type="button"
              onClick={() => toggle(g.key)}
              aria-expanded={isOpen}
              className={`inline-flex items-center gap-1.5 max-w-full text-left hover:text-ps-accent ${g.mono ? 'font-mono' : ''}`}
            >
              <span className="truncate">{g.resource}</span>
              <span
                aria-hidden="true"
                className={`text-ps-subtle transition-transform [&>svg]:w-4 [&>svg]:h-4 ${isOpen ? 'rotate-180' : ''}`}
              >
                <ChevronDownIc />
              </span>
              <span className="sr-only">{isOpen ? 'Hide' : 'Show'} individual charges</span>
            </button>
          ) : (
            g.resource
          )}
        </td>
      );
      const product = (
        <td>
          <Tag tone={prod.tone}>{prod.label}</Tag>
        </td>
      );
      rows.push(
        <tr key={g.key}>
          {dateFirst ? (
            <>
              {date}
              {resource}
              {product}
            </>
          ) : (
            <>
              {resource}
              {product}
              {date}
            </>
          )}
          <td className="text-ps-muted max-w-[380px] truncate" title={groupDetail(g)}>
            {groupDetail(g)}
          </td>
          <td className={`text-right ${amountCls(g.type)}`}>{signed(g.type, groupAmount(g, currency, rate))}</td>
        </tr>,
      );
      if (canExpand && isOpen) {
        g.items.forEach((tx) => {
          const p = parseDescription(tx.description);
          rows.push(
            <tr key={`${g.key}:${tx.id}`}>
              <td className="!bg-ps-chrome" />
              <td colSpan={3} className="!bg-ps-chrome text-ps-muted text-[13px] max-w-[600px] truncate" title={tx.description}>
                {format(new Date(tx.created_at), 'HH:mm')}
                {p.spec ? ` · ${p.spec}` : ''}
              </td>
              <td className="!bg-ps-chrome text-right text-ps-muted text-[13px]">
                {signed(tx.type, txAmount(tx, currency, rate))}
              </td>
            </tr>,
          );
        });
      }
    });
  }

  return (
    <div className="ps-card overflow-x-auto">
      <table className="ps-tbl">
        <thead>{head}</thead>
        <tbody>{rows}</tbody>
      </table>
      {footer && <div className="px-4 py-3 border-t border-ps-raised text-[13px] text-ps-subtle">{footer}</div>}
    </div>
  );
};

export default ChargesTable;
